import { useTranslation } from "react-i18next";

import type { DocumentationNotePresentation } from "./documentationNoteMetadata";
import type { DocumentationSignedNoteOption } from "./models";
import { WorkflowInstanceSwitcher } from "./WorkflowInstanceSwitcher";
import { WorkflowNodeResults } from "./WorkflowNodeResults";

import styles from "./DocumentationWorkspace.module.css";

type Props = {
    presentation: DocumentationNotePresentation;
    /** Date and time as they were written into the note body, so the screen cannot disagree with the signed text. */
    date?: string;
    time?: string;
    sourceNotes?: DocumentationSignedNoteOption[];
    onSourceNoteClick?: (noteId: string, projectId?: string) => void;
};

const noEdit = () => undefined;
const noConfirmation = () => undefined;

/**
 * Render an ISO timestamp the way the note body already spells it: Europe/Stockholm,
 * YYYY-MM-DD and HH:MM. The body is written server-side in that zone, so formatting in
 * the viewer's own locale would show a different day or an extra seconds field for
 * anyone outside Sweden - disagreeing with the text they are about to sign.
 */
export const stockholmDateTime = (timestamp?: string | number): { date: string; time: string } => {
    if (timestamp === undefined || timestamp === "") return { date: "", time: "" };
    const moment = new Date(timestamp);
    if (Number.isNaN(moment.getTime())) return { date: "", time: "" };
    const zone = { timeZone: "Europe/Stockholm" } as const;
    return {
        date: moment.toLocaleDateString("sv-SE", zone),
        time: moment.toLocaleTimeString("sv-SE", { ...zone, hour: "2-digit", minute: "2-digit" })
    };
};

/** Date and time header, because the workflow nodes below carry no date of their own. */
export const DocumentationNoteDateTime = ({ date, time }: { date?: string; time?: string }) => {
    const { t } = useTranslation();

    if (!date && !time) return null;

    return (
        <div className={styles.noteDateTime}>
            {date && (
                <span>
                    <strong>{t("notes.labels.date")}:</strong> {date}
                </span>
            )}
            {time && (
                <span>
                    <strong>{t("notes.labels.time")}:</strong> {time}
                </span>
            )}
        </div>
    );
};

export const DocumentationNoteView = ({ presentation, date, time, sourceNotes, onSourceNoteClick }: Props) => {
    const hasMultipleWorkflows = presentation.workflows.length > 1;

    return (
        <>
            {hasMultipleWorkflows ? (
                <WorkflowInstanceSwitcher
                    header={<DocumentationNoteDateTime date={date} time={time} />}
                    tabs={presentation.workflows.map((workflow, index) => ({
                        id: workflow.instanceId || `${workflow.workflowId || workflow.workflowTitle}-${index}`,
                        title: workflow.workflowTitle,
                        content: (
                            <section className={styles.workflowInstance}>
                                <header className={styles.workflowInstanceHeader}>
                                    <h2>{workflow.workflowTitle}</h2>
                                </header>
                                {workflow.reason ? <p className={styles.workflowMatchReason}>{workflow.reason}</p> : null}
                                <WorkflowNodeResults
                                    nodes={workflow.nodes}
                                    edits={{}}
                                    confirmations={new Set<string>()}
                                    disabled
                                    readOnly
                                    sourceNotes={sourceNotes}
                                    onSourceNoteClick={onSourceNoteClick}
                                    onEdit={noEdit}
                                    onConfirmation={noConfirmation}
                                />
                            </section>
                        )
                    }))}
                />
            ) : (
                <>
                    <DocumentationNoteDateTime date={date} time={time} />
                    <WorkflowNodeResults
                        nodes={presentation.nodes}
                        edits={{}}
                        confirmations={new Set<string>()}
                        disabled
                        readOnly
                        sourceNotes={sourceNotes}
                        onSourceNoteClick={onSourceNoteClick}
                        onEdit={noEdit}
                        onConfirmation={noConfirmation}
                    />
                </>
            )}
        </>
    );
};
